import { ControlProps, CSSObjectWithLabel, GroupBase, MultiValueProps, OptionProps } from 'react-select';

import { DropdownOption } from '../DropdownInput/typings';

export const getControlStyles = <
  Option = DropdownOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  base: CSSObjectWithLabel,
  props: ControlProps<Option, IsMulti, Group>,
  colors: { gray200: string; blue500: string; red500: string },
  isInvalid: boolean,
) => {
  const borderColor = props.isFocused ? colors.blue500 : colors.red500;

  return {
    ...base,
    fontSize: '0.875rem',
    minHeight: 'auto',
    borderColor: isInvalid ? borderColor : colors.gray200,
    boxShadow: isInvalid ? `0 0 0 1px ${borderColor}` : 'none',
    overflow: 'hidden',
    borderTopRightRadius: '0.25rem',
    borderBottomRightRadius: '0.25rem',
    ':hover': { borderColor: isInvalid ? borderColor : colors.gray200 },
    ':focus': { borderColor: colors.blue500, boxShadow: `0 0 0 1px ${colors.blue500}` },
  };
};

export const getValueContainerStyles = (base: CSSObjectWithLabel) => ({
  ...base,
  padding: '0.1875rem 0 0.1875rem 0.75rem',
  gap: '0.25rem',
});

export const getPlaceholderStyles = (base: CSSObjectWithLabel) => ({
  ...base,
  margin: '0 0.125rem',
  lineHeight: '1.5rem',
});

export const getOptionStyles = <
  Option = DropdownOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  base: CSSObjectWithLabel,
  props: OptionProps<Option, IsMulti, Group>,
  colors: { blue50: string; blue500: string },
) => ({
  ...base,
  fontSize: '0.875rem',
  backgroundColor: props.isSelected ? `${colors.blue500} !important` : 'white',
  ':hover': { backgroundColor: colors.blue50 },
});

export const getMultiValueStyles = <
  Option = DropdownOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  base: CSSObjectWithLabel,
  props: MultiValueProps<Option, IsMulti, Group>,
  colors: { blue50: string; blue100: string; blue800: string },
) => ({
  ...base,
  margin: 0,
  borderRadius: '6px',
  backgroundColor: colors.blue100,
  color: colors.blue800,
  opacity: props.isDisabled ? 0.5 : 1,
});

export const getMultiValueLabel = (base: CSSObjectWithLabel, colors: { blue800: string }) => ({
  ...base,
  fontSize: '14px',
  fontWeight: 500,
  padding: 0,
  paddingLeft: '0.75rem',
  color: colors.blue800,
  height: '1.5rem',
  lineHeight: '1.5rem',
});

export const getMultiValueRemove = (
  base: CSSObjectWithLabel,
  colors: { blue100: string; gray400: string; blue800: string },
) => ({
  ...base,
  backgroundColor: colors.blue100,
  color: colors.blue800,
  paddingLeft: '0.5rem',
  paddingRight: '0.5rem',
  borderRadius: '6px',
  opacity: 0.5,
  ':hover': {
    color: colors.gray400,
    cursor: 'pointer',
  },
});

export const getDropdownIndicator = (base: CSSObjectWithLabel) => ({
  ...base,
  padding: '0 0.5rem',
  minHeight: '1.875rem',
  display: 'flex',
  alignItems: 'center',
});
